'use client'

import { LoadingSpinner } from '@/components/ui/loading-spinner'
import { SuperscaleLogomark } from '@/components/icons/superscale-logomark'
import { useCounter } from '@/utils/hooks/use-counter'
import { motion } from 'framer-motion'
import { useEffect, useRef } from 'react'

const ANIMATION_COOLDOWN_MS = 30_000

// Track when a LoadingScreen was last shown. When one unmounts and another mounts
// shortly after (e.g. auth loaded → data loading), we skip the entrance animation
// to prevent a visible flicker between screens. After 30s the animation plays again.
let lastShownAt = 0

/**
 * Survives the share-link → research hop so Brand/Product `LoadingScreen`s keep the same subtitle
 * without each caller threading a prop through layouts they don't own. Cleared when the destination
 * is ready (see `useResearchAdPreview`).
 */
let pendingSubtitle: string | null = null

export function setPendingLoadingScreenSubtitle(subtitle: string | null) {
    pendingSubtitle = subtitle
}

export type ILoadingScreenProps = {
    error?: {
        showAfter: number
        onError: () => void
    }
    delay?: number
    /** Shown under the spinner. Falls back to `setPendingLoadingScreenSubtitle` across route hops. */
    subtitle?: string
}

export const LoadingScreen = ({ error, delay, subtitle }: ILoadingScreenProps) => {
    const synced = useRef(false)
    const shouldAnimate = useRef(Date.now() - lastShownAt > ANIMATION_COOLDOWN_MS)
    const resolvedSubtitle = subtitle ?? pendingSubtitle

    useEffect(() => {
        lastShownAt = Date.now()
        return () => {
            lastShownAt = Date.now()
        }
    }, [])

    useCounter({
        start: 0,
        end: error?.showAfter ?? 5,
        onComplete: error?.onError,
        startPredicate: !synced.current && !!error,
    })

    return (
        <motion.div
            className="min-h-dvh fixed inset-0 flex flex-col items-center justify-center bg-zinc-50 z-[9999]"
            initial={false}
        >
            <motion.div
                className="flex flex-col items-center gap-4"
                initial={shouldAnimate.current ? { opacity: 0, y: 28 } : false}
                animate={{ opacity: 1, y: 0 }}
                transition={
                    shouldAnimate.current
                        ? {
                              delay: (delay ?? 0) + 0.15,
                              opacity: { duration: 0.6, ease: 'easeOut' },
                              y: { duration: 0.9, ease: [0.16, 1, 0.3, 1] },
                          }
                        : { duration: 0 }
                }
            >
                <SuperscaleLogomark className="h-8 w-auto" />
                <LoadingSpinner size={20} colorClassName="stroke-zinc-600" />
                {resolvedSubtitle ? <p className="text-sm text-zinc-500">{resolvedSubtitle}</p> : null}
            </motion.div>
        </motion.div>
    )
}
